import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, take } from 'rxjs';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { UserManagementService } from '../../services/user-management.service';
import * as UserManagementActions from '../actions/user-management.actions';
import { selectRights, selectRoles, selectUsers } from '../selectors/user-management.selectors';

@Injectable()
export class UserManagementEffects {
    private readonly actions$ = inject(Actions);
    private readonly userManagementService = inject(UserManagementService);
    private readonly store = inject(Store);

    // Load Users Effect
    loadUsers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.loadUsers),

            mergeMap(() =>
                this.userManagementService
                    .getUsers()
                    .pipe(take(1))
                    .pipe(
                        map(users => UserManagementActions.loadUsersSuccess({ users })),
                        catchError(error => {
                            console.error('Error loading users in effect:', error);
                            return of(UserManagementActions.loadUsersFailure({ error: error.message }));
                        })
                    )
            )
        )
    );

    // Load Roles Effect
    loadRoles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.loadRoles),

            mergeMap(() =>
                this.userManagementService.getCustomerRoles().pipe(
                    take(1),

                    map(roles => UserManagementActions.loadRolesSuccess({ roles })),
                    catchError(error => {
                        console.error('Error loading roles in effect:', error);
                        return of(UserManagementActions.loadRolesFailure({ error: error.message }));
                    })
                )
            )
        )
    );

    // Load Rights Effect
    loadRights$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.loadRights),

            mergeMap(() =>
                this.userManagementService.getCustomerRights().pipe(
                    take(1),

                    map(assignedRights => UserManagementActions.loadRightsSuccess({ assignedRights })),
                    catchError(error => {
                        console.error('Error loading rights in effect:', error);
                        return of(UserManagementActions.loadRightsFailure({ error: error.message }));
                    })
                )
            )
        )
    );

    // Load User Rights Effect
    loadUserRights$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.loadUserRights),

            mergeMap(({ userId }) =>
                this.userManagementService.getUserRights(userId).pipe(
                    take(1),

                    map(rights => UserManagementActions.loadUserRightsSuccess({ rights })),
                    catchError(error => {
                        console.error('Error loading user rights in effect:', error);
                        return of(UserManagementActions.loadUserRightsFailure({ error: error.message }));
                    })
                )
            )
        )
    );

    // Create Role Effect
    createRole$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.createRole),
            mergeMap(({ role }) =>
                this.userManagementService.createRole(role).pipe(
                    take(1),
                    map(createdRole => UserManagementActions.createRoleSuccess({ role: createdRole })),
                    catchError(error => of(UserManagementActions.createRoleFailure({ error: error.message })))
                )
            )
        )
    );

    // Update Role Effect
    updateRole$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.updateRole),
            mergeMap(({ role }) =>
                this.userManagementService.updateRole(role.id, role).pipe(
                    take(1),
                    map(updatedRole => UserManagementActions.updateRoleSuccess({ role: updatedRole })),
                    catchError(error => of(UserManagementActions.updateRoleFailure({ error: error.message })))
                )
            )
        )
    );

    // Delete Role Effect
    deleteRole$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.deleteRole),
            mergeMap(({ roleId }) =>
                this.userManagementService.deleteRole(roleId).pipe(
                    take(1),
                    map(() => UserManagementActions.deleteRoleSuccess({ roleId })),
                    catchError(error => of(UserManagementActions.deleteRoleFailure({ error: error.message })))
                )
            )
        )
    );

    // Update User Rights Effect
    updateUserRights$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.updateUserRights),
            mergeMap(({ userId, rights }) =>
                this.userManagementService.updateUserRights(userId, rights).pipe(
                    take(1),
                    map(() => UserManagementActions.updateUserRightsSuccess({ rights })),
                    catchError(error => of(UserManagementActions.updateUserRightsFailure({ error: error.message })))
                )
            )
        )
    );

    // Smart loading effects - only load if data doesn't exist
    ensureUsersLoaded$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.ensureUsersLoaded),
            withLatestFrom(this.store.select(selectUsers)),
            mergeMap(([, users]) => {
                if (!users || users.length === 0) {
                    return this.userManagementService.getUsers().pipe(
                        map(users => UserManagementActions.loadUsersSuccess({ users })),
                        catchError(error => of(UserManagementActions.loadUsersFailure({ error: error.message })))
                    );
                }
                return of({ type: '[User Management] Users Already Loaded' });
            })
        )
    );

    ensureRolesLoaded$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.ensureRolesLoaded),
            withLatestFrom(this.store.select(selectRoles)),
            mergeMap(([, roles]) => {
                if (!roles || roles.length === 0) {
                    return this.userManagementService.getCustomerRoles().pipe(
                        map(roles => UserManagementActions.loadRolesSuccess({ roles })),
                        catchError(error => of(UserManagementActions.loadRolesFailure({ error: error.message })))
                    );
                }
                return of({ type: '[User Management] Roles Already Loaded' });
            })
        )
    );

    ensureRightsLoaded$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserManagementActions.ensureRightsLoaded),
            withLatestFrom(this.store.select(selectRights)),
            mergeMap(([, rights]) => {
                if (!rights || rights.length === 0) {
                    return this.userManagementService.getCustomerRights().pipe(
                        map(assignedRights => UserManagementActions.loadRightsSuccess({ assignedRights })),
                        catchError(error => of(UserManagementActions.loadRightsFailure({ error: error.message })))
                    );
                }
                return of({ type: '[User Management] Rights Already Loaded' });
            })
        )
    );
}
